<template>
  <MainContainer>
    <LanguageSelect
      v-model="selectedLanguage"
      :langs="langs"
      :contentFilled="filledLangs"
      style="top: 0"
      @copyFromDefault="handleCopyFromDefault"
      @clear="handleClearLang"
    />
    <div class="settings">
      <a-form-model :model="currentForm" ref="settingsForm">
        <div class="grid">
          <a-form-model-item class="theme-selector" :colon="false" required>
            <template #label>
              <span>Theme</span>
              <a-button
                type="link"
                :disabled="isColorMatchTheme"
                @click="setIsModalVisible(true)"
              >
                Save as template
              </a-button>
            </template>
            <a-select
              v-model="currentForm.theme"
              size="large"
              @change="onThemeChange"
            >
              <a-select-opt-group label="Basic">
                <a-select-option
                  v-for="theme in customThemes.filter((t) => t.is_default)"
                  :value="theme.id"
                  :key="theme.id"
                >
                  {{ theme.name }}
                </a-select-option>
              </a-select-opt-group>
              <a-select-opt-group label="Custom">
                <a-select-option value="custom">Custom</a-select-option>
                <a-select-option
                  v-for="theme in customThemes.filter((t) => !t.is_default)"
                  :value="theme.id"
                  :key="theme.id"
                >
                  {{ theme.name }}
                </a-select-option>
              </a-select-opt-group>
            </a-select>
            <span v-if="!isColorMatchTheme" style="color: #f5222e">
              Save current theme as new template
            </span>
          </a-form-model-item>
        </div>
        <div v-if="!systemThemeSelected && !selectedTheme?.is_default" class="grid">
          <a-form-model-item :colon="false" label="Custom theme" required>
            <a-radio-group
              v-model="currentForm.schema"
            >
              <a-radio value="light">Light</a-radio>
              <a-radio value="dark">Dark</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </div>
        <div class="grid">
          <a-form-model-item :colon="false" label="Match screen theme">
            <a-radio-group
              v-model="currentForm.matchTheme"
              :disabled="true"
            >
              <a-radio value="inherit">Inherited from match pages</a-radio>
              <a-radio value="system">System</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </div>

        <div class="divider" />

        <div class="section">
          <div class="section__header">
            <h4 class="section__title">Theme settings</h4>
          </div>

          <template v-if="systemThemeSelected">
            <div class="unavailable">
              <img src="/resources/images/unavailable.svg" alt="Unavailable" />
              <span>Color selection is unavailable in system theme</span>
            </div>
          </template>
          <template v-else>
            <div class="block">
              <h5 class="block__title">
                Header buttons
              </h5>
              <div class="grid mb-2 header-overlay">
                <a-form-model-item :colon="false" required>
                  <template #label>
                    Theme button
                    <a-tooltip slot="suffix">
                      <template slot="title">
                        <span>
                          The <strong>Light overlay</strong> is designed for use
                          with dark banners. The
                          <strong>Dark overlay</strong> is better suited for
                          light banners.
                        </span>
                      </template>
                      <a-icon type="info-circle" class="text-primary" />
                    </a-tooltip>
                  </template>
                  <a-radio-group
                    v-model="currentForm.headerButton.overlaySchema"
                  >
                    <a-radio value="light">Light</a-radio>
                    <a-radio value="dark">Dark</a-radio>
                  </a-radio-group>
                </a-form-model-item>
              </div>
              <div class="grid">
                <a-form-model-item label="Text color" :colon="false" required>
                  <input-color-picker
                    :color="currentForm.headerButton.textColor"
                    :disabled="themeSettingsDisabled"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.headerButton.textColor = val)"
                  />
                </a-form-model-item>
                <a-form-model-item
                  label="Background color"
                  :colon="false"
                  required
                >
                  <input-color-picker
                    :color="currentForm.headerButton.bgColor"
                    :disabled="themeSettingsDisabled"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.headerButton.bgColor = val)"
                  />
                </a-form-model-item>
              </div>
            </div>

            <div class="block">
              <h5 class="block__title">Main</h5>
              <div class="grid">
                <a-form-model-item label="Main color" :colon="false" required>
                  <input-color-picker
                    :color="currentForm.main.mainColor"
                    :disabled="themeSettingsDisabled"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.main.mainColor = val)"
                  />
                </a-form-model-item>
                <a-form-model-item label="Fill" :colon="false" required>
                  <a-select
                    v-model="currentForm.main.fill"
                    size="large"
                    :disabled="themeSettingsDisabled"
                  >
                    <a-select-option value="solid">Solid</a-select-option>
                    <a-select-option value="gradient">
                      Gradient
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
                <a-form-model-item
                  label="Background color"
                  :colon="false"
                  required
                >
                  <input-color-picker
                    :color="currentForm.main.bgColor"
                    :disabled="themeSettingsDisabled"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.main.bgColor = val)"
                  />
                </a-form-model-item>
                <a-form-model-item
                  label="Overlay color"
                  :colon="false"
                  required
                >
                  <input-color-picker
                    :color="currentForm.main.overlayColor"
                    :disabled="themeSettingsDisabled"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.main.overlayColor = val)"
                  />
                </a-form-model-item>
                <a-form-model-item label="Text color" :colon="false" required>
                  <input-color-picker
                    :color="currentForm.main.textColor"
                    :disabled="themeSettingsDisabled"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.main.textColor = val)"
                  />
                </a-form-model-item>
                <a-form-model-item label="Icon color" :colon="false" required>
                  <input-color-picker
                    :color="currentForm.main.iconColor"
                    :disabled="themeSettingsDisabled"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.main.iconColor = val)"
                  />
                </a-form-model-item>
              </div>
            </div>

            <div class="block">
              <h5 class="block__title">Filter</h5>
              <div class="grid">
                <a-form-model-item label="Active color" :colon="false" required>
                  <input-color-picker
                    :color="currentForm.filter.activeColor"
                    :disabled="themeSettingsDisabled"
                    position="top"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.filter.activeColor = val)"
                  />
                </a-form-model-item>
                <a-form-model-item
                  label="Content on active color"
                  :colon="false"
                  required
                >
                  <input-color-picker
                    :color="currentForm.filter.contentColor"
                    :disabled="themeSettingsDisabled"
                    position="top"
                    disableAlpha
                    merged
                    @input="(val) => (currentForm.filter.contentColor = val)"
                  />
                </a-form-model-item>
              </div>
            </div>
          </template>
        </div>
      </a-form-model>
      <CustomThemeModal
        v-model="isCustomThemeModalVisible"
        :theme="currentForm"
        @close="setIsModalVisible(false)"
        @submit="handleSubmitCustomTheme"
      />
    </div>
  </MainContainer>
</template>
<script>
import MainContainer from '@/components/main/mainContainer'
import InputColorPicker from '@/components/custom/colorPicker/inputColorPicker'
import LanguageSelect from '@/components/custom/languageSelect.vue'

import CustomThemeModal from '@/views/apps/custom-pages/constructor/settings/customThemeModal.vue'

import apiClient from '@/services/axios'
import {
  isFormColorsMatchTheme,
  DTOToFormColors,
  formColorsToDTO,
} from '@/services/utils/custom-pages'

import { DEFAULT_LANG } from '@/constants/lang'
import { CUSTOM_PAGE_SETTINGS_FIELD } from '@/constants/custom-pages'

import { mapState, mapGetters, mapActions } from 'vuex'

import isEqual from 'lodash/isEqual'

export default {
  components: {
    MainContainer,
    InputColorPicker,
    LanguageSelect,
    CustomThemeModal,
  },
  computed: {
    ...mapState({
      langs: (state) => state.langs.langData,
    }),
    ...mapGetters('settings', [
      'selectedLanguage',
      'form',
      'currentForm',
      'isCustomThemeModalVisible',
      'customThemes',
    ]),
    selectedLanguage: {
      get() {
        return this.$store.getters['settings/selectedLanguage']
      },
      set(value) {
        this.setSelectedLanguage(value)
      },
    },
    themeSettingsDisabled() {
      return this.currentForm.matchTheme === 'system'
    },
    selectedTheme() {
      return this.customThemes.find((t) => t.id === this.currentForm.theme)
    },
    systemThemeSelected() {
      return (
        this.selectedTheme?.name === 'System' || this.selectedTheme?.id === 1
      )
    },
    currentFormRaw() {
      const raw =
        this.$store.state.customPages.settings.form[this.selectedLanguage]
      return raw ? JSON.parse(JSON.stringify(raw)) : {}
    },
    isColorMatchTheme() {
      if (this.systemThemeSelected) return true

      return isFormColorsMatchTheme(this.currentForm, this.selectedTheme)
    },
    filledLangs() {
      const defaultThemeDTO = DTOToFormColors(this.defaultTheme)

      const res = this.langs.reduce((obj, { name }) => {
        const { theme, matchTheme, ...form } = this.form[name]
        obj[name] = !isEqual(form, defaultThemeDTO)

        return obj
      }, {})

      return res
    },
    defaultTheme() {
      const theme = this.customThemes.find(t => t.is_default && t.name.toLowerCase() === 'system')

      return theme
    },
  },
  methods: {
    ...mapActions('settings', [
      'setSelectedLanguage',
      'setIsModalVisible',
      'fetchThemes',
      'setCurrentForm',
      'setLangForm',
    ]),
    setTheme(theme, lang) {
      theme = theme ?? {}

      const normalizedTheme = DTOToFormColors(theme)

      if (lang) {
        this.setLangForm({
          data: {
            ...this.form[lang],
            ...normalizedTheme,
          },
          lang,
        })
      } else {
        this.setCurrentForm({
          ...this.currentForm,
          ...normalizedTheme,
        })
      }
    },
    onThemeChange(themeId) {
      if (typeof themeId === 'number') {
        const theme = this.customThemes.find((t) => t.id === themeId)

        this.setTheme(theme)
      }
    },
    async handleSubmitCustomTheme(data) {
      const payload = {
        name: data.name,
        ...formColorsToDTO(this.currentForm),
      }

      try {
        const resp = await apiClient.post('/admin/custom-page/theme', payload)

        if (resp?.data?.data?.id) {
          await this.fetchThemes()
          this.currentForm.theme = resp.data.data.id
          this.setIsModalVisible(false)
        }
      } catch (e) {
        console.error(e)
      }
    },
    handleCopyFromDefault() {
      const defaultForm = this.form[DEFAULT_LANG]

      this.form[this.selectedLanguage] = JSON.parse(
        JSON.stringify(defaultForm),
      )
    },
    handleClearLang(lang) {
      this.form[lang] = { ...CUSTOM_PAGE_SETTINGS_FIELD, ...DTOToFormColors(this.defaultTheme) }

      this.$refs.settingsForm.clearValidate()
    },
    setLangsTheme() {
      this.langs.forEach((lang) => {
        const form = this.form[lang.name]

        if (isEqual(form, CUSTOM_PAGE_SETTINGS_FIELD)) {
          this.setTheme(this.defaultTheme, lang.name)
        } else {
          const themeObj = this.customThemes.find((t) => t.id === this.form?.[lang.name]?.theme)
          this.setTheme(themeObj, lang.name)
        }
      })
    },
  },
  created() {
    this.setLangsTheme()
  },
  watch: {
    currentFormRaw: {
      handler(newVal, oldVal) {
        if (!oldVal) return

        const selectedTheme = this.customThemes.find((t) => t.id === newVal.theme)

        if (!selectedTheme || selectedTheme.id === 1 || selectedTheme.name === 'System') {
          return
        }

        const { ...selectedThemeColors } = DTOToFormColors(selectedTheme)
        const { theme, matchTheme, ...newColors } = newVal

        if (selectedTheme.is_default && !isEqual(newColors, selectedThemeColors)) {
          this.currentForm.theme = 'custom'
        }
      },
      deep: true,
    },
  },
}
</script>
<style lang="scss" scoped>
.settings {
  margin-bottom: 32px;

  .theme-selector ::v-deep .ant-form-item-label {
    width: 100%;
    text-align: left;
    position: relative;

    label {
      position: static;
    }

    .ant-btn-link {
      position: absolute;
      right: 0;
      top: 0;
      padding: 0;
      border: none;
      height: auto;
      line-height: 22px;
    }
  }

  .grid {
    max-width: 856px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  .divider {
    width: 100%;
    height: 1px;
    background-color: #d9d9d9;
    margin-bottom: 24px;
  }

  .section__title {
    font-size: 18px;
    line-height: 28px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 12px;
  }

  .unavailable {
    img {
      display: block;
      margin: 0 auto;
      height: 100px;
    }

    span {
      display: block;
      color: #595959;
      font-size: 14px;
      line-height: 22px;
      text-align: center;
      margin-top: 12px;
    }
  }

  .block {
    ::v-deep .ant-form-item {
      margin-bottom: 0;
    }

    &:not(:last-child) {
      margin-bottom: 24px;
    }

    ::v-deep .ant-form-item-label {
      overflow: initial;
    }
  }

  .block__title {
    font-size: 14px;
    line-height: 22px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 12px;
  }

  .header-overlay {
    ::v-deep label {
      margin-bottom: 0;

      i {
        margin-left: 4px;
      }
    }

    ::v-deep .ant-tooltip-inner {
      width: 250px;

      span {
        font-weight: normal;
        white-space: initial;
      }
    }
  }
}
</style>
